// @flow

import React, { useState, useEffect, useRef } from 'react';
import styled, { type StyledComponent } from 'styled-components';
import { __ } from '../../../i18n/l10n';
import { useDispatch } from 'react-redux';
import { isMobile } from '../../../utils/LayoutUtils';
import { WS_DISPLAY_ERROR_LAYER } from '../../../store/actions';
import config from '../../../config/app.config.js';
import { Button } from './Button';
import { Icon } from './Icon';
import { Modal } from './Modal';
import { DropZone } from './DropZone';
import type { Option, File } from '../types';

type InputFileProps = {|
  className?: string,
  label: string,
  types?: Option[],
  id?: string,
  onChange?: (event: File | null) => void,
  isDisabled?: boolean,
|};

const InputFileRaw = ({
  className,
  label,
  types = [],
  id = '',
  onChange,
  isDisabled = false,
}: InputFileProps) => {
  const [uploadModalIsOpen, setUploadModalIsOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const nativeFileInput = useRef<HTMLInputElement | null>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    setUploadModalIsOpen(false);
    if (onChange) {
      onChange(file);
    }
  }, [file, onChange]);

  const openUploadModal = () => {
    if (!isDisabled) setUploadModalIsOpen(true);
  };

  const openNativeFileInput = () => {
    if (nativeFileInput.current) {
      nativeFileInput.current.click();
    }
  };

  const onFileSelection = (files) => {
    if (files.length > 1) {
      dispatch({
        type: WS_DISPLAY_ERROR_LAYER,
        payload: {
          message: __('You can only upload one file at a time. Choose wisely!'),
        },
      });
      return;
    }

    const selectedFile = files[0];
    if (selectedFile) {
      const { name, type, size } = selectedFile;
      if (!types || types.length === 0 || types.find((item) => item.value === type)) {
        if (size / 1000 > config.upload.maxSize) {
          setFile(null);
          // __('Your file is too big. Try again with a file smaller than %1$s Mb.')
          dispatch({
            type: WS_DISPLAY_ERROR_LAYER,
            payload: {
              message: __(
                'Your file is too big. Try again with a file smaller than %1$s Mb.',
                config.upload.maxSize / 1000
              ),
            },
          });
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          if (e.target instanceof FileReader && typeof e.target.result === 'string') {
            setFile({
              name,
              type,
              data: e.target.result,
            });
          }
        };
        reader.readAsDataURL(selectedFile);
      } else {
        setFile(null);
        // __('This file format is invalid. Please try in one of the following formats: %1$s.')
        dispatch({
          type: WS_DISPLAY_ERROR_LAYER,
          payload: {
            message: __(
              'This file format is invalid. Please try in one of the following formats: %1$s.',
              types.map((loopType) => loopType.label.toUpperCase()).join(', ')
            ),
          },
        });
      }
    } else {
      setFile(null);
    }
  };

  const onFileInputChange = (event) => {
    onFileSelection(event.target.files);
  };

  if (isMobile()) {
    return (
      <span className={className}>
        <label className="label" htmlFor={id}>
          <input
            className="nativeInput"
            onChange={onFileInputChange}
            ref={nativeFileInput}
            type="file"
            id={id}
            accept={types && types.map((type) => type.value).join(',')}
          />
          <Button id={`${id}Button`} onClick={openNativeFileInput}>
            {label} <Icon name="scan" />
          </Button>
        </label>
      </span>
    );
  }

  return (
    <span className={className}>
      <Button id={`${id}Button`} onClick={openUploadModal} isDisabled={isDisabled}>
        {label} <Icon name="upload" />
      </Button>
      <Modal className="popin" isOpen={uploadModalIsOpen} onClose={setUploadModalIsOpen}>
        <DropZone id={`${id}DropZone`} types={types} onFileSelection={onFileSelection}>
          {__('Click & drag your file here')}
        </DropZone>
      </Modal>
    </span>
  );
};

export const InputFile: StyledComponent<InputFileProps, {}, typeof InputFileRaw> = styled(
  InputFileRaw
)`
  position: relative;

  .label {
    position: relative;
    display: block;
  }

  .nativeInput {
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    width: 100%;
    height: calc(100% - 10px);
    z-index: 2;
    opacity: 0;
  }
`;
